import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Layout, Dropdown, Avatar, Button, Spin, Input, Form } from "antd";
import Modal from "./Modal";
import { usePost, usePut, useGet } from "../hooks/useApi";
import { API_ENDPOINTS } from "../config/api";
import Toast from "../utils/toast";
import { useTranslation } from "react-i18next";
import { ChevronsLeft, ChevronsRight } from "lucide-react";

const { Header: AntHeader } = Layout;

const Header = ({ isSidebarVisible, toggleSidebar }) => {
  const { t, i18n } = useTranslation();
  const { user, logout, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const {
    data: profileData,
    loading: profileLoading,
    execute: fetchProfile,
  } = useGet(API_ENDPOINTS.PROFILE, false);

  const { post: changePassword, loading: changingPassword } = usePost(
    API_ENDPOINTS.CHANGE_PASSWORD,
  );
  const { put: updateProfile, loading: updatingProfile } = usePut(
    API_ENDPOINTS.UPDATE_PROFILE,
  );

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profileModalOpen && profileData?.data?.user) {
      const u = profileData.data.user;
      profileForm.setFieldsValue({
        full_name: u.full_name || "",
        email: u.email || "",
      });
    }
  }, [profileModalOpen, profileData, profileForm]);

  const handleLogout = () => {
    logout();
    navigate("/login");
    Toast.success(t("header.loggedOutSuccess"));
  };

  const handleProfileClick = () => {
    setProfileModalOpen(true);
  };

  const handleChangePassword = () => {
    passwordForm.resetFields();
    setPasswordModalOpen(true);
  };

  const submitPasswordChange = async (values) => {
    try {
      const response = await changePassword({
        old_password: values.old_password,
        new_password: values.new_password,
      });
      if (response?.status === true) {
        Toast.success(response?.message || t("header.passwordChangedSuccess"));
        setPasswordModalOpen(false);
        passwordForm.resetFields();
      } else {
        Toast.error(response?.message || t("header.failedChangePassword"));
      }
    } catch (error) {
      Toast.error(error.message || t("header.failedChangePassword"));
    }
  };

  const onPasswordModalConfirm = () => {
    passwordForm
      .validateFields()
      .then(submitPasswordChange)
      .catch(() => {});
  };

  const handleProfileSubmit = async (values) => {
    try {
      const response = await updateProfile(values);
      if (response?.status === true) {
        Toast.success(response?.message || t("header.profileUpdatedSuccess"));
        setProfileModalOpen(false);
        await fetchProfile();
        if (refreshUserData) await refreshUserData();
      } else {
        // Check for email already exists error
        const errorMsg = response?.message || t("header.failedUpdateProfile");

        // Parse error message if it contains email already exists
        if (
          errorMsg.toLowerCase().includes("email") &&
          (errorMsg.toLowerCase().includes("already exists") ||
            errorMsg.toLowerCase().includes("already taken") ||
            errorMsg.toLowerCase().includes("duplicate"))
        ) {
          // Set field-specific error on email field
          profileForm.setFields([
            {
              name: "email",
              errors: [t("header.emailAlreadyRegistered")],
            },
          ]);
        } else {
          // General error
          Toast.error(errorMsg);
        }
      }
    } catch (error) {
      console.error("Profile update error:", error);

      // Check if error response contains email already exists
      const errorMsg = error.message || t("header.failedUpdateProfile");
      const errorResponse = error.response?.data;

      if (
        errorResponse?.email ||
        errorMsg.toLowerCase().includes("email already exists") ||
        errorMsg.toLowerCase().includes("email already taken")
      ) {
        profileForm.setFields([
          {
            name: "email",
            errors: [t("header.emailAlreadyRegistered")],
          },
        ]);
      } else {
        Toast.error(errorMsg);
      }
    }
  };

  const onProfileModalConfirm = () => {
    profileForm
      .validateFields()
      .then(handleProfileSubmit)
      .catch(() => {});
  };

  const getUserName = () => {
    if (profileData?.data?.user?.full_name)
      return profileData.data.user.full_name;
    if (user?.name) return user.name;
    return t("header.adminUser");
  };

  const getUserEmail = () => {
    if (profileData?.data?.user?.email) return profileData.data.user.email;
    if (user?.email) return user.email;
    return "admin@example.com";
  };

  const getUserInitial = () => {
    const name = getUserName();
    return name.charAt(0).toUpperCase();
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "nl" : "en";
    i18n.changeLanguage(newLang);
  };

  const dropdownMenuItems = [
    {
      key: "profile",
      label: t("header.manageProfile"),
      onClick: handleProfileClick,
    },
    {
      key: "password",
      label: t("header.changePassword"),
      onClick: handleChangePassword,
    },
    { type: "divider" },
    {
      key: "logout",
      label: t("header.logout"),
      danger: true,
      onClick: () => setLogoutModalOpen(true),
    },
  ];

  return (
    <AntHeader
      className="app-header"
      style={{
        padding: "0 20px",
        background: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        borderBottom: "1px solid #e8e8e8",
      }}
    >
      <div
        className="app-header-left"
        style={{ display: "flex", alignItems: "center", gap: 10 }}
      >
        <Button
          type="text"
          onClick={toggleSidebar}
          className="app-header-toggle"
          style={{ color: "#000", fontWeight: 500, flexShrink: 0 }}
        >
          {isSidebarVisible ? <ChevronsLeft /> : <ChevronsRight />}
        </Button>
        <h2
          className="app-header-title"
          style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#000" }}
        >
          {t("header.welcomeBack")} {getUserName()}
        </h2>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {/* Language Toggle Button */}
        <Button
          type="default"
          onClick={toggleLanguage}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontWeight: 600,
            borderRadius: 20,
            padding: "4px 16px",
            height: 36,
            border: "1px solid #d9d9d9",
            background: "#f5f5f5",
          }}
        >
          {/* {i18n.language === "en" ? "🇬🇧" : "🇳🇱"}{" "}
          {i18n.language === "en" ? "EN" : "NL"} */}
          {i18n.language === "en" ? "English" : "Dutch"}{" "}
          {i18n.language === "en" ? "EN" : "NL"}
        </Button>

        <Dropdown
          menu={{ items: dropdownMenuItems }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <div
            className="app-header-profile"
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
          
            <div
              className="app-header-user-info"
              style={{ textAlign: "right", minWidth: 0 }}
            >
              <div
                className="app-header-name"
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: "#000",
                  lineHeight: "18px",
                }}
              >
                {profileLoading ? t("common.loading") : getUserName()}
              </div>
              <div
                className="app-header-email"
                style={{ fontSize: 12, color: "#333", lineHeight: "18px" }}
              >
                {profileLoading ? "..." : getUserEmail()}
              </div>
            </div>
  {profileLoading ? (
              <Spin size="small" />
            ) : (
              <Avatar style={{ backgroundColor: "#066744", color: "#fff" }}>
                {getUserInitial()}
              </Avatar>
            )}
          </div>
        </Dropdown>
      </div>

      <Modal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        title={t("header.manageProfile")}
        onConfirm={onProfileModalConfirm}
        confirmText={
          updatingProfile ? t("header.updating") : t("header.updateProfile")
        }
        confirmDisabled={updatingProfile}
      >
        <Form form={profileForm} layout="vertical" requiredMark={false}>
          <Form.Item
            name="full_name"
            label={t("common.name")}
            rules={[{ required: true, message: t("header.pleaseEnterName") }]}
          >
            <Input placeholder={t("header.enterYourName")} size="large" />
          </Form.Item>

          <Form.Item
            name="email"
            label={t("common.email")}
            rules={[
              { required: true, message: t("header.pleaseEnterEmail") },
              { type: "email", message: t("header.pleaseEnterValidEmail") },
            ]}
          >
            <Input
              type="email"
              placeholder={t("header.enterYourEmail")}
              size="large"
            />
          </Form.Item>

          {profileData?.data?.phone && (
            <Form.Item label={t("header.phoneNumber")}>
              <Input
                value={profileData.data.phone}
                readOnly
                size="large"
                style={{ background: "#f5f5f5" }}
              />
              <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                {t("header.phoneManagedSeparately")}
              </div>
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        title={t("header.changePassword")}
        onConfirm={onPasswordModalConfirm}
        confirmText={
          changingPassword
            ? t("header.changingPassword")
            : t("header.changePasswordBtn")
        }
        confirmDisabled={changingPassword}
      >
        <Form form={passwordForm} layout="vertical" requiredMark={false}>
          <Form.Item
            name="old_password"
            label={t("header.currentPassword")}
            rules={[
              {
                required: true,
                message: t("header.pleaseEnterCurrentPassword"),
              },
            ]}
          >
            <Input.Password
              placeholder={t("header.enterCurrentPassword")}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="new_password"
            label={t("header.newPassword")}
            rules={[
              { required: true, message: t("header.pleaseEnterNewPassword") },
              { min: 8, message: t("header.passwordMinLength") },
            ]}
          >
            <Input.Password
              placeholder={t("header.enterNewPassword")}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label={t("header.confirmNewPassword")}
            dependencies={["new_password"]}
            rules={[
              { required: true, message: t("header.pleaseConfirmNewPassword") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("new_password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(t("header.passwordsDoNotMatch")),
                  );
                },
              }),
            ]}
          >
            <Input.Password
              placeholder={t("header.confirmNewPasswordPlaceholder")}
              size="large"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        isOpen={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        title={t("header.confirmLogout")}
        onConfirm={handleLogout}
        confirmText={t("header.logout")}
        confirmColor="danger"
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: 48, marginBottom: 20 }}>⚠️</div>
          <h3 style={{ marginBottom: 15 }}>
            {t("header.logoutConfirmMessage")}
          </h3>
          <p style={{ color: "#666" }}>{t("header.logoutRedirectMessage")}</p>
        </div>
      </Modal>
    </AntHeader>
  );
};

export default Header;
